import type { QueryClient } from "@tanstack/react-query";

// Lightweight bridge between the raw fetch client (api.ts) and React Query.
// The ProtectedRoute gate reads the billing-subscription query reactively,
// so flipping its cached value to `null` (isSubscriptionExpired treats null
// as expired) is enough to force every mounted gate to re-decide.
//
// We can't subscribe-to-React from inside api.ts, hence this tiny module.

let client: QueryClient | null = null;

export function setSubscriptionQueryClient(qc: QueryClient): void {
  client = qc;
}

/**
 * Signal that the currently-assumed-active subscription is no longer valid
 * according to the backend. Invoked by the API layer when a request returns
 * HTTP 402 (canonical "payment required") or 403 with
 * code="SUBSCRIPTION_EXPIRED".
 *
 * Any ProtectedRoute currently rendered will re-evaluate and send the user
 * to /settings (if they can manage billing) or show the blocking overlay.
 * We also invalidate so a real billing fetch reconciles the state.
 */
export function markSubscriptionExpired(): void {
  if (!client) return;
  // Overwrite every billing-subscription cache entry (keyed by org+outlet)
  // with an authoritative null so the gate flips immediately.
  client.setQueriesData({ queryKey: ["billing-subscription"] }, null);
  client.invalidateQueries({ queryKey: ["billing-subscription"] });
}

// Shape the backend uses in response bodies to signal subscription-related
// 403s. Exposed so the API layer can match without magic strings scattered
// around.
export const SUBSCRIPTION_EXPIRED_CODE = "SUBSCRIPTION_EXPIRED";

export function bodyIndicatesSubscriptionExpiry(body: unknown): boolean {
  if (!body || typeof body !== "object") return false;
  const code = (body as { code?: unknown }).code;
  return code === SUBSCRIPTION_EXPIRED_CODE;
}
